import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useUser } from 'store/User/Context';
import styled from 'styled-components';
import { brokerApiRequests } from 'utils/api-requests/broker';
import { commonApiRequests } from 'utils/api-requests/common';
import { flipApiRequests } from 'utils/api-requests/flip';
import { saleOffersApiRequests } from 'utils/api-requests/sale';
import { isTouchDevice } from 'utils/checkIsTouchDevice';
import { encodeData } from 'utils/encodeAndDecodeFunctions';
import { formatFilterPrice } from 'utils/formatFilterPrice';
import { formatPriceBeforeModification } from 'utils/formatters/formatPriceBeforeModification';
import { getSubModuleByType } from 'utils/formatters/getSubModuleByType';
import { normalizeFlipResult } from 'utils/formatters/normalizeFlipResult';
import { generateSingleOfferLink } from 'utils/generateSingleOfferLink';
import {
    SaleOfferDuplicatesResponse,
    SaleOfferHashResponse,
    SaleOfferResponseContent
} from 'utils/state-managment/sale/saleOffer';
import { CalculatorSettings, initialCalculatorState } from 'utils/types/Calculator';
import { UserReactions } from 'utils/types/OfferData';
import { ReactionType } from 'utils/types/ReactionType';
import { Module, NoteDto, SaleOpinionResult } from 'utils/types/SaleModels';
import { hasRole, UserRole } from 'utils/userRoleHelper';

import Indicator from 'components/atom/Indicator';
import OfferActions from 'components/common/Card/common/OfferActions/OfferActions';
import {
    InteractiveElements
} from 'components/common/Card/common/V2InteractiveIconsWrapper/V2InteractiveIconsWrapper';
import { IconEnum } from 'components/common/Icon';
import SingleOfferDetailsRenderer, {
    SingleOfferDetailsRendererProps
} from 'components/common/SingleOfferDetailsRenderer/SingleOfferDetailsRenderer';
import { SliderImages } from 'components/common/Slider/Slider';
import { V2DuplicatesTabProps } from 'components/common/Tabs/tabs/V2DuplicatesTab';
import { ExtendedNote } from 'components/common/Tabs/tabs/V2NoteTab';
import { TabType } from 'components/common/Tabs/V2Tabs';
import { convertToThumbnailPath, createImagesForSlider } from 'components/functions/imagesFunctions';
import {
    Offer,
    saleOfferListFunctions
} from 'components/functions/offerListFunctions/saleOfferListFunctions/saleOfferListFunctions';
import { generateOfferActionsElements } from 'components/functions/shared/generateOfferActionsElements';

const Wrapper = styled.div`
    margin: 15px;
`;

const NoAdvertContainer = styled.h1`
    text-align: center;
    margin-top: 15%;
`;

interface OfferElementState {
    hover: boolean;
    clicked: boolean;
}

const SingleSaleDetailPage = () => {
    const { user } = useUser();
    const { hash, module } = useParams();
    const [preparedAdvert, setPreparedAdvert] = useState<SingleOfferDetailsRendererProps>();
    const [moreButtonActiveId, setMoreButtonActiveId] = useState<string | null>(null);
    const [offer, setOffer] = useState<SaleOfferHashResponse>();
    const [isLoading, setIsLoading] = useState(true);
    const [duplicates, setDuplicates] = useState<SaleOfferDuplicatesResponse[]>();
    const [notes, setNotes] = useState();
    const [comments, setComments] = useState<SaleOpinionResult>();
    const [infoElementsState, setInfoElementsState] = useState<Record<string, OfferElementState>>({});
    const [detailsInfoElementsState, setDetailsInfoElementsState] = useState<Record<string, OfferElementState>>({});
    const [activeInfoElementId, setActiveInfoElementId] = useState<string | null>(null);
    const [activeDetailInfoElementId, setActiveDetailInfoElementId] = useState<string | null>(null);
    const [calculatorState, setCalculatorState] = useState<CalculatorSettings>(initialCalculatorState);
    const hasPredictionRole = hasRole(UserRole.PREDICT_PRICE, user.userRole);
    const hasCalculationRole = hasRole(UserRole.CALCULATOR, user.userRole);

    const setInfoElementHover = (offerId: number, elementKey: string, hover: boolean) => {
        const key = `${offerId}-${elementKey}`;
        setInfoElementsState((prev) => ({
            ...prev,
            [key]: {
                ...prev[key],
                hover
            }
        }));

        if (hover) {
            setActiveInfoElementId(key);
        } else if (activeInfoElementId === key) {
            setActiveInfoElementId(null);
        }
    };

    const setInfoElementClicked = (offerId: number, elementKey: string, clicked: boolean) => {
        const key = `${offerId}-${elementKey}`;
        setInfoElementsState((prev) => ({
            ...prev,
            [key]: {
                ...prev[key],
                clicked
            }
        }));

        if (clicked) {
            setActiveInfoElementId(key);
            setActiveInfoElementId(null);
        }
    };

    const setDetailsInfoElementHover = (offerId: number, elementKey: string, hover: boolean) => {
        const key = `${offerId}-${elementKey}`;
        setDetailsInfoElementsState((prev) => ({
            ...prev,
            [key]: {
                ...prev[key],
                hover
            }
        }));

        if (hover) {
            setActiveDetailInfoElementId(key);
        } else if (activeDetailInfoElementId === key) {
            setActiveDetailInfoElementId(null);
        }
    };

    const setDetailsInfoElementClicked = (offerId: number, elementKey: string, clicked: boolean) => {
        const key = `${offerId}-${elementKey}`;
        setInfoElementsState((prev) => ({
            ...prev,
            [key]: {
                ...prev[key],
                clicked
            }
        }));

        if (clicked) {
            setActiveDetailInfoElementId(key);
        } else if (activeDetailInfoElementId === key) {
            setActiveDetailInfoElementId(null);
        }
    };

    const deleteOpinion = async (id: number, module: Module, opinionId: number) => {
        const response = await saleOffersApiRequests.deleteOpinion(opinionId, module);

        if (response) {
            setPreparedAdvert((prev) => {
                return {
                    ...prev,
                    detailsCardData: {
                        ...prev!.detailsCardData,
                        tabsContent: {
                            ...prev!.detailsCardData.tabsContent,
                            opinionTabContent: {
                                ...prev!.detailsCardData.tabsContent.opinionTabContent!,
                                comments: prev!.detailsCardData.tabsContent.opinionTabContent!.comments!.filter((x) => x.id !== opinionId),
                                commentsCount: response.numberOfCommunityComments,
                                opinionsNumber: response.numberOfCommunityOpinions,
                                opinionsRating: response.communityScore || 0
                            }
                        }
                    }
                };
            });
        }
    };

    const handleChangeReaction = async (name: keyof ReactionType, value: boolean, offer: SaleOfferHashResponse) => {
        const userReaction = Object.assign({}, offer.reaction);

        if (name === 'likeStatus' && userReaction.unlikeStatus && value) {
            userReaction.unlikeStatus = false;
        } else if (name === 'unlikeStatus' && userReaction.likeStatus && value) {
            userReaction.likeStatus = false;
        }

        (userReaction[name] as boolean) = value;
        const city = offer.location;

        const response = await commonApiRequests.setOfferReaction(offer.advertisementId, city, userReaction as UserReactions, offer.module);
        setPreparedAdvert((prev) => ({
            ...prev,
            detailsCardData: {
                ...prev!.detailsCardData,
                interactiveElements: prev!.detailsCardData.id === offer.advertisementId ? generateInteractiveElements({ ...offer, reaction: response }) : prev!.detailsCardData.interactiveElements,
                tabsContent: {
                    ...prev!.detailsCardData.tabsContent,
                    duplicatesTabContent: {
                        ...prev!.detailsCardData.tabsContent.duplicatesTabContent,
                        duplicates: prev!.detailsCardData.tabsContent.duplicatesTabContent?.duplicates?.map((x) => {
                            if (x.id === offer.advertisementId) {
                                return {
                                    ...x,
                                    cardData: {
                                        ...x.cardData,
                                        interactiveElements: generateInteractiveElements({
                                            ...offer,
                                            reaction: response
                                        })
                                    }
                                };
                            }

                            return x;
                        })
                    }
                }
            }
        }));
    };

    const handleSaveNote = async (value: string, advertId: number, type: Module, id?: number) => {
        const response = await commonApiRequests.updateNote(type, value, advertId, id || 0);

        if (response) {
            if (id) {
                setPreparedAdvert((prev) => {
                    return {
                        ...prev,
                        detailsCardData: {
                            ...prev!.detailsCardData,
                            tabsContent: {
                                ...prev!.detailsCardData.tabsContent,
                                notesTabContent: {
                                    ...prev!.detailsCardData.tabsContent.notesTabContent!,
                                    notes: prev!.detailsCardData.tabsContent.notesTabContent!.notes!.map((x) => {
                                        if (x.noteId === id) {
                                            return {
                                                ...x,
                                                content: value
                                            };
                                        }

                                        return x;
                                    })
                                }
                            }
                        }
                    };
                });
            } else {
                setPreparedAdvert((prev) => {
                    return {
                        ...prev,
                        detailsCardData: {
                            ...prev!.detailsCardData,
                            tabsContent: {
                                ...prev!.detailsCardData.tabsContent,
                                notesTabContent: {
                                    ...prev!.detailsCardData.tabsContent.notesTabContent!,
                                    notes: prev!.detailsCardData.tabsContent.notesTabContent!.notes ? [response].concat(prev!.detailsCardData.tabsContent.notesTabContent!.notes as unknown as ExtendedNote) : [response]
                                },
                                numberOfNotes: (prev!.detailsCardData.tabsContent.numberOfNotes ?? 0) + 1
                            }
                        }
                    };
                });
            }
        }
    };

    const handleReservation = async (days: number, advertisementId: number, module: Module, permanently?: boolean) => {
        const response = permanently
            ? await brokerApiRequests.addBlockage({
                advertisementId,
                module
            })
            : await brokerApiRequests.addTempBlockage({
                advertisementId,
                days,
                module
            });

        if (response) {
            setOffer((prev) => {
                if (!prev) return prev;

                return {
                    ...prev,
                    blockageInfo: {
                        ...response,
                        module: response.module as Module,
                        notificationStatus: response.notificationStatus,
                        myBlockage: true
                    }
                };
            });
        }
    };

    const removeReservation = async (advertisementId: number, module: Module) => {
        const response = await brokerApiRequests.removeBlockage({
            advertisementId,
            module
        });

        if (response) {
            setOffer((prev) => {
                if (!prev) return prev;

                return {
                    ...prev,
                    blockageInfo: null
                };
            });
        }
    };

    const handleNotificationBlockedOfferToggle = (offerId: number, module: Module, currentStatus: boolean) => {
        if (currentStatus) {
            brokerApiRequests.deactiveNotificationAboutEndBlockage(offerId, module);
        } else {
            brokerApiRequests.activeNotificationAboutEndBlockage(offerId, module);
        }

        setOffer((prev) => {
            if (!prev) return prev;

            return {
                ...prev,
                blockageInfo: prev.blockageInfo
                    ? {
                        ...prev.blockageInfo,
                        notificationStatus: !currentStatus
                    }
                    : null
            };
        });
    };

    const generateOrganizationCardData = (offer: SaleOfferHashResponse) => {
        return {
            isReserved: offer.blockageInfo !== null,
            ...offer.blockageInfo
                ? {
                    reservationDate: offer.blockageInfo.blockedDate,
                    userName: offer.blockageInfo.userName,
                    isPermanentlyReserved: offer.blockageInfo.permanentlyBlocked,
                    myBlockage: offer.blockageInfo.myBlockage
                }
                : [],
            handleReservation: (days: number) => {
                handleReservation(days, offer.advertisementId, offer.module);
            },
            removeReservation: () => {
                removeReservation(offer.advertisementId, offer.module);
            },
            handleReservationPermanently: () => {
                handleReservation(0, offer.advertisementId, offer.module, true);
            },
            handleToggleReservation: () => {
                handleNotificationBlockedOfferToggle(
                    offer.advertisementId,
                    offer.module,
                    offer?.blockageInfo?.notificationStatus ?? false
                );
            },
            notificationStatus: offer.blockageInfo?.notificationStatus,
            offerId: offer.advertisementId,
            offerModule: offer.module
        };
    };

    const generateInteractiveElements = (offer: SaleOfferHashResponse): InteractiveElements[] => {
        const offerLink = generateSingleOfferLink(offer.encryptedId, 'sale', getSubModuleByType(offer.module));
        const isMoreButtonActive = moreButtonActiveId === offer.advertisementId.toString();
        const similarOfferAction = {
            children: <><p>Pokaż podobne ogłoszenia</p></>,
            onClick: async (e: MouseEvent) => {
                e.stopPropagation();
                const filterData = await saleOffersApiRequests.getSimilarOffersFilter(offer.advertisementId, offer.module);

                if (filterData) {
                    const encoded = encodeData({ ...filterData, locations: filterData.locations || [], _timestamp: Date.now() });
                    window.open(`${window.location.origin}/ai-filter/${encoded}`, '_blank');
                }
            }
        };
        const offerActionsElements = generateOfferActionsElements(offerLink, offer.link, [similarOfferAction]);
        const originalEncryptedId = offer.originalEncryptedId !== null && offer.originalEncryptedId !== undefined;
        const originalOfferLink = `${window.location.origin}/sale/${getSubModuleByType(offer.module)}/${offer.originalEncryptedId}`;

        if (originalEncryptedId) {
            offerActionsElements.push({
                children:
                <>
                    <p>Przejdź do oryginalnego ogłoszenia</p>
                </>,
                onClick: async (e: MouseEvent) => {
                    e.stopPropagation();

                    window.open(originalOfferLink, '_blank');
                }
            });
        }

        return [
            {
                tooltipText: 'Wyświetlono',
                icon: IconEnum.EYE,
                active: offer.reaction?.viewedStatus,
                disabled: offer.reaction?.viewedStatus === undefined,
                onClick: () => handleChangeReaction('viewedStatus', !offer.reaction?.viewedStatus, offer)
            },
            {
                tooltipText: 'Lubię to',
                icon: IconEnum.LIKE,
                active: offer.reaction?.likeStatus,
                onClick: () => handleChangeReaction('likeStatus', !offer.reaction?.likeStatus, offer)
            },
            {
                tooltipText: 'Nie lubię tego',
                icon: IconEnum.DISLIKE,
                active: offer.reaction?.unlikeStatus,
                disabled: offer.reaction?.unlikeStatus === undefined,
                onClick: () => handleChangeReaction('unlikeStatus', !offer.reaction?.unlikeStatus, offer)
            },
            {
                tooltipText: 'Notatki',
                icon: IconEnum.MESSAGE,
                active: offer.numberOfNotes ? offer.numberOfNotes > 0 : false,
                counter: offer.numberOfNotes ? offer.numberOfNotes || 1 : undefined,
                openTabOnClick: TabType.NOTES
            },
            {
                tooltipText: 'Ogłoszenie nieaktualne',
                icon: IconEnum.BLOCKED,
                active: offer.reaction?.hideStatus,
                disabled: offer.reaction?.hideStatus === undefined,
                onClick: () => handleChangeReaction('hideStatus', !offer.reaction?.hideStatus, offer)
            },
            {
                tooltipText: 'Przeprowadzona rozmowa',
                icon: IconEnum.PHONE,
                active: offer.reaction?.phoneStatus,
                disabled: offer.reaction?.phoneStatus === undefined,
                onClick: () => handleChangeReaction('phoneStatus', !offer.reaction?.phoneStatus, offer)
            },
            {
                tooltipText: 'Umówione spotkanie',
                icon: IconEnum.PEOPLES_3,
                active: offer.reaction?.arrangedStatus,
                disabled: offer.reaction?.arrangedStatus === undefined,
                onClick: () => handleChangeReaction('arrangedStatus', !offer.reaction?.arrangedStatus, offer)
            },
            ...offer.phoneNumber
                ? [
                    {
                        icon: IconEnum.PHONE,
                        isLink: true,
                        active: false,
                        isPhoneBtn: false,
                        text: <a href={`tel:${offer.phoneNumber}`}>{offer.phoneNumber}</a>
                    }
                ]
                : [],
            {
                tooltipText: 'Przejdź do ogłoszenia',
                icon: IconEnum.LINK,
                isLink: true,
                noText: true,
                text: <a target="_blank" rel="noopener noreferrer" href={offer.link}/>
            },
            ...offer.photos && offer.photos.length > 0
                ? [
                    {
                        icon: IconEnum.PHOTOS,
                        isModalOpen: true,
                        isLink: true,
                        text: <p>Zobacz zdjęcia</p>,
                        isPhotoBtn: true,
                        visibleDuringModal: true,
                        sliderElements: {
                            photos: createImagesForSlider(offer.photos, offer.module) as SliderImages[],
                            title: offer.title
                        }
                    }
                ]
                : [],
            {
                icon: 'squares' as IconEnum,
                tooltipText: isTouchDevice() ? undefined : 'Więcej',
                onClick: () => isMoreButtonActive ? setMoreButtonActiveId(null) : setMoreButtonActiveId(offer.advertisementId.toString()),
                additionalJSXSibling: isMoreButtonActive ? <OfferActions onClickOutside={() => setMoreButtonActiveId(null)} isInModal elements={offerActionsElements} /> : undefined
            }
        ];
    };

    const handleDelete = async (noteId: number, id: number, type: Module) => {
        const response = await commonApiRequests.deleteNote(type, noteId);

        if (response) {
            setPreparedAdvert((prev) => {
                return {
                    ...prev,
                    detailsCardData: {
                        ...prev!.detailsCardData,
                        tabsContent: {
                            ...prev!.detailsCardData.tabsContent,
                            notesTabContent: {
                                ...prev!.detailsCardData.tabsContent.notesTabContent!,
                                notes: prev!.detailsCardData.tabsContent.notesTabContent!.notes!.filter((x) => x.noteId !== noteId)
                            },
                            numberOfNotes: prev!.detailsCardData.tabsContent.numberOfNotes! - 1
                        }
                    }
                };
            });
        }
    };

    const handleSaveOpinion = async (module: Module, commentContent: string, rating: number, advertisementId: number, opinionId?: number) => {
        if (opinionId) {
            const response = await saleOffersApiRequests.updateOpinion(opinionId, commentContent, rating, module);

            if (response) {
                setPreparedAdvert((prev) => {
                    return {
                        ...prev,
                        detailsCardData: {
                            ...prev!.detailsCardData,
                            tabsContent: {
                                ...prev!.detailsCardData.tabsContent,
                                opinionTabContent: {
                                    ...prev!.detailsCardData.tabsContent.opinionTabContent!,
                                    comments: prev!.detailsCardData.tabsContent.opinionTabContent!.comments!.map((x) => {
                                        if (x.id === opinionId) {
                                            return {
                                                ...x,
                                                content: commentContent,
                                                rating
                                            };
                                        }

                                        return x;
                                    }),
                                    opinionsNumber: response.numberOfCommunityOpinions,
                                    opinionsRating: response.communityScore || 0
                                }
                            }
                        }
                    };
                });
            }
        } else {
            const response = await saleOffersApiRequests.addOpinion(advertisementId, commentContent, rating, module);
            const newOpinions = await saleOffersApiRequests.getOpinions(advertisementId, module);

            if (response) {
                setPreparedAdvert((prev) => {
                    return {
                        ...prev,
                        detailsCardData: {
                            ...prev!.detailsCardData,
                            tabsContent: {
                                ...prev!.detailsCardData.tabsContent,
                                opinionTabContent: {
                                    ...prev!.detailsCardData.tabsContent.opinionTabContent!,
                                    comments: newOpinions?.opinions?.map((opinion) => ({
                                        id: opinion.opinionId,
                                        date: opinion.dateAdded,
                                        content: opinion.content || '',
                                        rating: opinion.numberOfStars,
                                        isUserComment: opinion.loggedUserOpinion
                                    })),
                                    commentsCount: response.numberOfCommunityComments,
                                    opinionsNumber: response.numberOfCommunityOpinions,
                                    opinionsRating: response.communityScore || 0
                                }
                            }
                        }
                    };
                });
            }
        }
    };

    const loadFlipCalculations = async (offerId: number) => {
        if (!offer) return;

        const flipDefaultValues = await flipApiRequests.getFlipIndividualOfferValues(offerId);

        if (Object.keys(flipDefaultValues).length > 0) {
            setCalculatorState(flipDefaultValues as CalculatorSettings);
        }
    };

    const updateFlipCalculations = async (offerId: number, data: CalculatorSettings) => {
        if (!offer) return;

        const requestData = { ...data, saleId: offerId };
        const response = await flipApiRequests.createFlipIndividualOfferValues(requestData);

        if (Object.keys(response).length > 0) {
            setCalculatorState(response as CalculatorSettings);

            await updateOfferFlipResults();
        }
    };

    const resetFlipCalculations = async (offerId: number) => {
        const response = await flipApiRequests.deleteFlipIndividualOfferValues(offerId);

        if (Object.keys(response).length > 0) {
            setCalculatorState(response as CalculatorSettings);

            await updateOfferFlipResults();
        }
    };

    const resetFlipCalculationField = async (offerId: number, fieldName: keyof CalculatorSettings) => {
        const requestData = { ...calculatorState, [fieldName]: null };
        const response = await flipApiRequests.updateDefaultFlipValues(requestData);

        if (Object.keys(response).length > 0) {
            setCalculatorState(response as CalculatorSettings);
            await updateOfferFlipResults();
        }
    };

    const handleCalculatorChange = (fieldName: keyof CalculatorSettings, value: string) => {
        setCalculatorState((prev) => ({
            ...prev,
            [fieldName]: value
        }));
    };

    const updateOfferFlipResults = async () => {
        if (!offer) return;

        const updatedOffer = await saleOffersApiRequests.getSaleOfferByHashId(hash, module);
        setOffer((prev) => prev ? { ...prev, flipResult: updatedOffer.flipResult } : prev);
    };

    useEffect(() => {
        setIsLoading(true);
        saleOffersApiRequests.getSaleOfferByHashId(hash, module).then(async (offer) => {
            setOffer(offer);
            const duplicates = await saleOffersApiRequests.getSaleOfferDuplicates(offer.module, offer.advertisementId);
            const notes = await commonApiRequests.getNotes(offer.module, offer.advertisementId.toString());
            const comments = await saleOffersApiRequests.getOpinions(offer.advertisementId, offer.module);

            setDuplicates(duplicates);
            setNotes(notes);
            setComments(comments);
            setIsLoading(false);
        }).catch(() => {
            setPreparedAdvert(undefined);
            setIsLoading(false);
        });
    }, [hash]);

    useEffect(() => {
        if (offer && comments) {
            const generateDuplicateCardData = (offer: SaleOfferDuplicatesResponse) => {
                return {
                    preTitle: offer.predictedLocation ? `${offer.location}, ` : offer.location,
                    oldPrice: formatPriceBeforeModification(offer.priceBeforeModification),
                    date: offer.dateAdded,
                    title: offer.title,
                    withPrediction: offer.module === Module.SALE_FLAT ? true : false,
                    infoElements: saleOfferListFunctions.generateInfoElements(offer as SaleOfferResponseContent, infoElementsState, hasPredictionRole, setInfoElementHover, setInfoElementClicked),
                    interactiveElements: generateInteractiveElements(offer as SaleOfferHashResponse),
                    modifications: {
                        modificationsNumber: offer.numberOfModifications ?? 0,
                        boostsNumber: offer.numberOfRaises ?? 0
                    },
                    note: offer.note,
                    modificationDate: offer.lastUpdated,
                    thumbnailPath: offer.photos ? offer.photos[0] : undefined,
                    ratingScore: offer.score ?? 0,
                    additionalInfoBadge: offer.offerFrom,
                    images: createImagesForSlider(offer.photos || [], offer.module!),
                    mainValue: offer.price ? `${formatFilterPrice(offer.price)} zł` : '- zł',
                    calculationValue: offer.flipResult && offer.flipResult.maxPurchasePrice ? offer.flipResult.maxPurchasePrice.toString() : undefined,
                    hasCalculationRole,
                    predictedPrice: offer.module === Module.SALE_FLAT && offer.predictedPrice ? offer.predictedPrice.toString() : undefined,
                    hasPredictionRole,
                    link: {
                        url: offer.link,
                        name: offer.portal
                    }
                };
            };
            const duplicatesTabContent: V2DuplicatesTabProps = {
                duplicates: duplicates?.map((duplicate) => {
                    return {
                        id: duplicate.advertisementId,
                        bigImage: true,
                        cardData: generateDuplicateCardData({ ...duplicate, module: offer.module }),
                        onClick: () => {
                        },
                        openOfferTab: () => {
                        }
                    };
                })
            };
            const detailsTabContent = saleOfferListFunctions.generateOfferDetailsTabContent({
                ...offer as Offer,
                note: offer.note as unknown as NoteDto,
                hasCalculationRole,
                hasPredictionRole,
                detailedContent: { description: offer.description, metaValue: offer.metaValue }
            } as Offer, detailsInfoElementsState, hasPredictionRole, setDetailsInfoElementHover, setDetailsInfoElementClicked);
            const flipInfo = normalizeFlipResult(offer.flipResult);

            const detailsCardData = {
                id: offer.advertisementId,
                interactiveElements: generateInteractiveElements(offer),
                modificationDate: offer.lastUpdated,
                additionalInfoBadge: offer.offerFrom,
                predictedAdditionalInfoBadge: offer.predictedOfferFrom,
                thumbnailPath: offer.photos && offer.photos.length > 0 ? convertToThumbnailPath(offer.photos[0], offer.module) : './../defaultImg.png',
                date: offer.dateAdded!,
                dateIcon: saleOfferListFunctions.getDateIcon(offer.status),
                tabsContent: {
                    interactiveElements: generateInteractiveElements(offer),
                    detailsTabContent: {
                        ...detailsTabContent,
                        hasCalculationRole,
                        hasPredictionRole,
                        withPrediction: offer.module === Module.SALE_FLAT ? true : false,
                        score: offer.score ?? 0,
                        scorePrecision: offer.scorePrecision ?? 0,
                        showLendiButton: true,
                        isDuplicate: offer.originalEncryptedId !== null && offer.originalEncryptedId !== undefined,
                        lowestPriceLabel: offer.duplicateWithLowestPrice,
                        privateOfferLabel: offer.duplicateWithPrivateOffer,
                        archivedLabel: offer.archived,
                        adContext: { adId: offer.advertisementId, adModule: offer.module },
                        ...user.organizationRole ? { organizationContainer: generateOrganizationCardData(offer) } : {}
                    },
                    duplicatesTabContent: offer.originalEncryptedId || offer.numberOfDuplicates > 0 ? duplicatesTabContent : undefined,
                    modificationTabContent: {
                        modifications: offer.modifications || []
                    },
                    notesTabContent: {
                        notes: notes,
                        handleDelete: (noteId: string) => handleDelete(Number(noteId), offer.advertisementId, offer.module),
                        handleSaveNote: (value: string, id?: string) => handleSaveNote(value, offer.advertisementId, offer.module, Number(id))
                    },
                    calculationsTabContent: offer.module === Module.SALE_FLAT
                        ? {
                            calculation: true,
                            hasCalculationRole,
                            id: offer.advertisementId,
                            flipInfo,
                            currentPrice: offer.price,
                            loadFlipCalculations: () => loadFlipCalculations(offer.advertisementId),
                            calculatorState,
                            onValueChange: handleCalculatorChange,
                            onUpdateField: (fieldName: keyof CalculatorSettings, value: string) => updateFlipCalculations(offer.advertisementId, {
                                ...calculatorState,
                                [fieldName]: value
                            }),
                            onResetField: (fieldName: keyof CalculatorSettings) => resetFlipCalculationField(offer.advertisementId, fieldName),
                            onResetAll: () => resetFlipCalculations(offer.advertisementId)
                        }
                        : undefined,
                    opinionTabContent: {
                        handleSaveOpinion: (rating: number, content: string, id?: number) => handleSaveOpinion(offer.module, content, rating, offer.advertisementId, id || offer.myOpinion?.id),
                        comments: comments?.opinions?.map((opinion) => ({
                            id: opinion.opinionId,
                            date: opinion.dateAdded,
                            content: opinion.content || '',
                            rating: opinion.numberOfStars,
                            isUserComment: opinion.loggedUserOpinion
                        })),
                        commentsCount: offer.numberOfCommunityComments,
                        opinionsNumber: offer.numberOfCommunityOpinions,
                        opinionsRating: comments.communityScore || 0,
                        handleDeleteOpinion: (id: number) => deleteOpinion(offer.advertisementId, offer.module, id),
                        applicationScore: offer.score,
                        applicationScorePrecision: offer.scorePrecision
                    },
                    raisesTabContent: offer.raisesValue && offer.raisesValue && offer.raisesValue.length > 0 ? offer.raisesValue : undefined,
                    numberOfModifications: offer.numberOfModifications || 0,
                    numberOfDuplicates: offer.numberOfDuplicates || 0,
                    numberOfRaises: offer.numberOfRaises || 0,
                    numberOfNotes: offer.numberOfNotes || 0,
                    handleLoadDuplicates: async () => {},
                    handleLoadNotes: () => {
                    },
                    handleLoadComments: () => {
                    }
                }
            };

            setPreparedAdvert({ detailsCardData });
        }
    }, [
        offer,
        comments,
        moreButtonActiveId,
        activeInfoElementId,
        infoElementsState,
        activeDetailInfoElementId,
        calculatorState
    ]);

    return (
        <Wrapper>
            {isLoading
                ? <Indicator/>
                : preparedAdvert?.detailsCardData
                    ? <SingleOfferDetailsRenderer detailsCardData={preparedAdvert.detailsCardData}/>
                    : <NoAdvertContainer>Nie znaleziono ogłoszenia o podanym id.</NoAdvertContainer>}
        </Wrapper>
    );
};

export default SingleSaleDetailPage;
